// src/pages/UTRPeriodicity/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import GeneralSettings from "../../Components/Common/GeneralSettings";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";

// Page-specific Components
import GroupManager from "./components/GroupManager";

// API
import {
  getUTRPeriodicityData,
  generateUTRPeriodicityPlot,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useJobPolling } from "../../hooks/useJobPolling";
import { useGroupsManager } from "../../hooks/useGroupsManager";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useUTRPeriodicityShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

// Config
import {
  utrPeriodicityConfig,
  utrPeriodicityDefaults,
  utrPeriodicityValidation,
} from "../../config/plotConfigs/utrPeriodicityConfig";
import ActionButton from "../../Components/Common/ActionButton";

const UTRPeriodicity = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(true);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  const [apiData, setApiData] = useState({
    json_files: [],
    organism: "",
    transcriptome: "",
  });

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(utrPeriodicityDefaults, utrPeriodicityValidation);

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const { plotLoading, taskId, startJob } = useJobPolling();

  // Smart read length adjustment
  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  // Multi-group manager for UTR Periodicity (supports up to 32 groups)
  const {
    groups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    clearAllGroups,
    handleAutoFill,
    handleAutoLabel,
    resetGroups,
  } = useGroupsManager(
    Array.from({ length: 8 }, (_, i) => ({
      id: `group${i + 1}`,
      name: `Group ${i + 1}`,
      files: [],
    }))
  );

  // Use the UTR Periodicity specific short code handler
  const {autoRunPlot, 
         setAutoRunPlot,
         consumeUserPassedShortCode } =
    useUTRPeriodicityShortCode({
      location,
      updateMultipleSettings,
      resetGroups,
    });

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });


  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Load data on component mount
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await getUTRPeriodicityData(projectId);
        const data = response?.data || response || {};

        setApiData({
          json_files: data.json_files || [],
          organism: data.organism || "",
          transcriptome: data.transcriptome || "",
        });

        // Set default seq type
        const fileTypes = data.json_files
          ? [...new Set(data.json_files.map((x) => x.file_type))]
          : [];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        // Set default group
        const uniqueGroups = [
          ...new Set(
            data.json_files?.map((file) => file.subgroup).filter(Boolean) || []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Failed to load project data:", err);
        const errorMsg = "Failed to load project data";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, setSelectedSeqType, setSelectedGroup]);

  // Smart read length adjustment
  useEffect(() => {
    const activeFiles = groups.reduce(
      (acc, group) => [...acc, ...(group.files || [])],
      []
    );

    if (activeFiles.length > 0 && apiData.json_files?.length > 0) {
      if (
        shouldAutoAdjust("minread", settings.minread, settings.maxread) &&
        shouldAutoAdjust("maxread", settings.minread, settings.maxread)
      ) {
        const { min, max } = getStandardReadLengths(
          activeFiles,
          apiData.json_files
        );
        if (settings.minread !== min || settings.maxread !== max) {
          updateMultipleSettings({
            minread: min,
            maxread: max,
          });
          toast.info(`Read lengths auto-adjusted: Min=${min}, Max=${max}`, {
            autoClose: 3000,
          });
        }
      }
    }
  }, [
    groups,
    apiData.json_files,
    getStandardReadLengths,
    shouldAutoAdjust,
    updateMultipleSettings,
    settings.minread,
    settings.maxread,
  ]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
      });

      const hasFiles = groups.some((g) => g.files.length > 0);

      if (!plotLoading && !loading && hasFiles) {
        console.log(
          "All conditions met, generating UTR Periodicity analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerate();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    groups.map((g) => g.files.length).join(","),
    setAutoRunPlot,
  ]);

  // Computed values
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    let files = apiData.json_files;
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((x) => x.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((x) => x.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const types = [...new Set(apiData.json_files.map((x) => x.file_type))];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count: apiData.json_files.filter((x) => x.file_type === type).length,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const groupSet = new Set(
      apiData.json_files.map((x) => x.subgroup).filter(Boolean)
    );
    return Array.from(groupSet).map((group) => ({
      value: group,
      label: group,
      count: apiData.json_files.filter((x) => x.subgroup === group).length,
    }));
  }, [apiData.json_files]);

  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    const seqTypeFiles = filteredFiles.filter(
      (x) => x.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedSeqType, filteredFiles, selectedFiles]);

  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (x) => x.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Event handlers
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      markFieldAsUserModified(key);
    },
    [updateSetting, markFieldAsUserModified]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    clearSelection();
    resetGroups(
      Array.from({ length: 8 }, (_, i) => ({
        id: `group${i + 1}`,
        name: `Group ${i + 1}`,
        files: [],
      }))
    );
    setPlotData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetUserModifications, clearSelection, resetGroups]);

  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      const seqTypeFiles = filteredFiles.filter(
        (x) => x.file_type === selectedSeqType
      );
      const fileIds = seqTypeFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedSeqType, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (x) => x.subgroup === selectedGroup
      );
      const fileIds = groupFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const buildPayload = useCallback(() => {
    const buildMaster = () => {
      const masterDict = {};
      groups.forEach((group) => {
        if (group.files && group.files.length > 0) {
          masterDict[String(group.name)] = {
            file_ids: (group.files || []).map(String),
            file_names: (group.files || [])
              .map((fileId) => {
                const file = apiData.json_files.find(
                  (f) => f.file_id === fileId
                );
                return file ? String(file.file_name) : "";
              })
              .filter(Boolean),
          };
        }
      });
      return masterDict;
    };
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    // Use the UTR Periodicity specific payload creator
    return createPlotSpecificPayload.utrPeriodicity({
      settings,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
      buildMaster,
    });
  }, [settings, groups, projectId, apiData, consumeUserPassedShortCode]);

  // Generate analysis
  const handleGenerate = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    if (groups.filter((g) => g.files?.length > 0).length === 0) {
      toast.error("Please assign files to at least one group");
      return;
    }

    const payload = buildPayload();
    console.log("Submitting UTR Periodicity payload:", payload);

    const onSuccess = (result) => {
      setPlotData(result);
      setError(null);
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setPlotData(null);
    };

    startJob(generateUTRPeriodicityPlot, payload, onSuccess, onError);
  }, [plotLoading, validateSettings, groups, buildPayload, startJob]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "UTR Periodicity | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "UTR Periodicity" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  UTR Periodicity Analysis
                </h4>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={handleReset}
                  disabled={plotLoading}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Reset All
                </Button>
              </div>
            </Col>
          </Row>

          {/* Settings Row */}
          <Row className="mb-4">
            {/* Left: UTR Settings */}
            <Col lg={12}>
              <GeneralSettings
                config={utrPeriodicityConfig}
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                title="UTR Periodicity Settings"
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right: Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Group Management */}
          <Row className="mb-4">
            <Col lg={12}>
              <GroupManager
                groups={groups}
                allFiles={apiData.json_files}
                filteredFiles={filteredFiles}
                onAddFilesToGroup={addFilesToGroup}
                onRemoveFilesFromGroup={removeFilesFromGroup}
                onUpdateGroupName={updateGroupName}
                onClearGroup={clearGroup}
                onAutoFill={handleAutoFill}
                onAutoLabel={handleAutoLabel}
                onClearAll={clearAllGroups}
                maxGroups={32}
                defaultVisibleGroups={8}
              />
            </Col>
          </Row>

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div ref={loadingDivRef} 
                   className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  <i className="ri-group-line me-1"></i>
                  Active Groups:{" "}
                  <strong>
                    {groups.filter((g) => g.files?.length > 0).length}
                  </strong>{" "}
                  | Total Files:{" "}
                  <strong>
                    {groups.reduce((sum, g) => sum + (g.files?.length || 0), 0)}
                  </strong>
                  {groups.filter((g) => g.files?.length > 0).length === 0 ? (
                    <span className="text-warning ms-2">
                      Please assign files to at least one group
                    </span>
                  ) : (
                    <i className="ri-check-circle-line ms-2 text-success"></i>
                  )}
                  {taskId && (
                    <span className="ms-2">
                      - <strong>Task ID:</strong> {taskId}
                    </span>
                  )}
                </div>
                <ActionButton
                  onClick={handleGenerate}
                  loading={plotLoading}
                  buttonType="plot"
                />{" "}
              </div>
            </Col>
          </Row>

          {/* Results Container */}
          {(plotData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef} 
                  plotData={plotData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={groups.reduce(
                    (sum, g) => sum + (g.files?.length || 0),
                    0
                  )}
                  title="UTR Periodicity Analysis Results"
                  onRetry={handleGenerate}
                  showFileCount={true}
                  showDownloadOptions={true}
                />
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UTRPeriodicity;
